import de from "date-fns/locale/de";
import {useEffect, useState} from "react";
import DatePicker, {registerLocale} from "react-datepicker";
import {Controller, useForm} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import "react-datepicker/dist/react-datepicker.css";
import Modal from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import {getBeppolicemanGasStationZones, getBeppolicemanParkingZones, getTicketForEdit,} from "../../actions/violations";
import Button from "../../common/button/button";
import {isAralDeClientId} from "../../common/client/clientHelper";
import EntityDetail from "../../common/entityDetail/entityDetail";
import SelectWithLabel from "../../common/input/selectWithLabel";
import LabelWithHR from "../../common/labels/labelWithHR";
import {getOptionsForZone, vehicleTypeOptions} from "../../globals";
import {newModalStyle} from "../../reusable/styles";
import {ticketViolationTypeForZone} from "../../utils/parkingViolationTypeMapping";
import DriveOffDetailsModal from "./driveOffDetailsModal.js";
import Style from "./modalEditTicket.module.css";
import ModalPlateConflict from "./modalPlateConflict.js";

registerLocale("de", de);

const ModalEditTicket = (props) => {
  const dispatch = useDispatch();
  const { handleSubmit, control, register, watch } = useForm();
  const { ownersParkingZonesForEditingTicket } = useSelector(
    (state) => state.violations
  );
  const { ticketForEdit } = useSelector((state) => state.violations);
  const [plateConflict, setPlateConflict] = useState(null);

  const { ownersGasStationZonesForEditingTicket } = useSelector(
    (state) => state.violations
  );

  const [plateNumber, setPlateNumber] = useState(null);
  const [countryCode, setCountryCode] = useState(null);
  const [vehicleType, setVehicleType] = useState(null);
  const [paymentPeriod, setPaymentPeriod] = useState(0);
  const [reminderPaymentPeriod, setReminderPaymentPeriod] = useState(0);
  const [lastReminderPaymentPeriod, setLastReminderPaymentPeriod] = useState(0);
  const [defaultParkingZone, setDefaultParkingZone] = useState(null);
  const [defaultGasStationZone, setDefaultGasStationZone] = useState(null);
  const parkingZoneWatch = watch("parkingZone", null);
  const gasStationZoneWatch = watch("gasStationZone", null);
  const [date, setDate] = useState(new Date(props.ticket?.date));
  const [doDetailsModalOpen, setDoDetailsModalOpen] = useState(false);
  const [gasStationTicketDetails, setGasStationTicketDetails] = useState(null);
  const [pendingEditData, setPendingEditData] = useState(null);
  const [typeChangeConfirmOpen, setTypeChangeConfirmOpen] = useState(false);

  const [pickerHeight, setPickerHeight] = useState(50);

  useEffect(() => {
    dispatch(getTicketForEdit(props.ticket?.number));
    if (props.ticket?.parkingZone?.parkingZoneNumber) {
      dispatch(
        getBeppolicemanParkingZones(props.ticket?.reporter?.email?.toString())
      );
    }
    if (!isAralDeClientId(props.ticket?.gasStationZone?.beppoClient?.id) &&
      props.ticket?.gasStationZone?.number) {
      dispatch(
        getBeppolicemanGasStationZones(props.ticket?.reporter?.email?.toString())
      );
    }

  }, [props.ticket?.reporter?.email, dispatch]);

  useEffect(() => {
    setPlateNumber(ticketForEdit?.licensePlate);
    setCountryCode(ticketForEdit?.countryCode);
    setVehicleType(ticketForEdit?.vehicleType);
    setPaymentPeriod(ticketForEdit?.paymentPeriod);
    setReminderPaymentPeriod(ticketForEdit?.reminderPaymentPeriod);
    setLastReminderPaymentPeriod(ticketForEdit?.lastReminderPaymentPeriod);
    setDefaultParkingZone(ticketForEdit?.parkingZone?.parkingZoneNumber);
    setDefaultGasStationZone(ticketForEdit?.gasStationZone?.number);
    setGasStationTicketDetails(ticketForEdit?.gasStationTicketDetails || {});
  }, [ticketForEdit]);

  const changeData = async (doDetails = null) => {
    const id = ticketForEdit?.id;
    const pz = parkingZoneWatch !== null ? parseInt(parkingZoneWatch) : null;
    const gs = gasStationZoneWatch !== null ? parseInt(gasStationZoneWatch) : null;

    const data = {
      id,
      plateNumber,
      countryCode,
      vehicleType,
      date,
      parkingZoneNumber: pz,
      gasStationZoneNumber: gs,
      paymentPeriod,
      reminderPaymentPeriod,
      lastReminderPaymentPeriod,
    };

    if (doDetails) {
      data.gasStationTicketDetails = doDetails;
    } else if (gasStationTicketDetails) {
      data.gasStationTicketDetails = gasStationTicketDetails;
    }

    if (typeWillChange) {
      setPendingEditData(data);
      setTypeChangeConfirmOpen(true);
      return;
    }

    await submitEdit(data);
  };

  const submitEdit = async (data) => {
    setPendingEditData(data);
    const result = await props.editTicket(data);
    if (result?.error === "ACTIVE_TICKET_EXISTS") {
      setPlateConflict(result);
    }
  };

  const confirmTypeChange = () => {
    setTypeChangeConfirmOpen(false);
    submitEdit(pendingEditData);
  };

  const { t } = useTranslation();

  const pzZone = ownersParkingZonesForEditingTicket?.find(
    (pz) => pz.parkingZoneNumber === parseInt(defaultParkingZone)
  );

  const zoneChanged = !!defaultParkingZone &&
    ticketForEdit?.parkingZone?.parkingZoneNumber != defaultParkingZone;
  const mappedTicketViolationType = zoneChanged
    ? ticketViolationTypeForZone(pzZone?.parkingViolationType)
    : null;
  const typeWillChange = !!mappedTicketViolationType &&
    mappedTicketViolationType !== ticketForEdit?.ticketViolationType;

  const gsZone = ownersGasStationZonesForEditingTicket?.find(
    (gs) => gs.gasStationZoneNumber === defaultGasStationZone
  );

  const showDODetailsModal = () => {
    setDoDetailsModalOpen(true);
  };

  const closeDODetailsModal = () => {
    setDoDetailsModalOpen(false);
  };

  const handleDODetailsSave = (doDetails) => {
    setGasStationTicketDetails(doDetails);
    changeData(doDetails);
  };

  return (
    <>
      <Modal
        open={props.open}
        closeOnEsc
        onClose={props.close}
        center
        styles={newModalStyle({ width: "450px" })}
      >
        {typeChangeConfirmOpen && (
          <div className={Style.confirmView}>
            <div className="modal__title">{t("ModalTicketTypeChangeConfirm.title")}</div>
            <div className={Style.confirmMessage}>{t("ModalTicketTypeChangeConfirm.message")}</div>
            <div className={Style.confirmActions}>
              <Button
                text={t("ModalTicketTypeChangeConfirm.cancel")}
                onClick={() => setTypeChangeConfirmOpen(false)}
              />
              <Button
                text={t("ModalTicketTypeChangeConfirm.confirm")}
                onClick={confirmTypeChange}
              />
            </div>
          </div>
        )}
        {/* Keep the form mounted: remounting resets the defaultValue inputs. */}
        <form onSubmit={handleSubmit(changeData)} hidden={typeChangeConfirmOpen}>
          <div className="modal__title">{t("Violations.editTicket")}</div>

          <div>
            <LabelWithHR label={t("ModalEditTicket.plateNumber")} />
            <Controller
              name="plateNumber"
              control={control}
              render={() => (
                <input
                  className={Style.textStyleInputEditModal}
                  name="plateNumber"
                  onChange={(e) => {
                    setPlateNumber(e.target.value);
                  }}
                  defaultValue={plateNumber}
                />
              )}
            />
          </div>
            <div>
              <LabelWithHR label={t("ModalEditTicket.CountryCode")} />

              <Controller
                name="countryCode"
                control={control}
                render={({ field }) => {
                  const countryOptions = getOptionsForZone(props.zoneCountryCode);
                  return (
                    <select
                      className={Style.textStyleInputEditModal}
                      name="countryCode"
                      value={field.value || countryCode}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                        setCountryCode(e.target.value);
                      }}
                    >
                      {countryOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {t(option.labelKey)}
                        </option>
                      ))}
                    </select>
                  );
                }}
              />
            </div>
          <div>
            <LabelWithHR label={t("ModalEditTicket.VehicleType")} />
            <Controller
              name="vehicleType"
              control={control}
              render={({ field }) => (
                <select
                  className={Style.textStyleInputEditModal}
                  name="vehicleType"
                  value={field.value || vehicleType}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    setVehicleType(e.target.value);
                  }}
                >
                  {vehicleTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {t(option.labelKey)}
                    </option>
                  ))}
                </select>
              )}
            />
          </div>
          {defaultGasStationZone && (
            <>
              <SelectWithLabel
                name="gasStationZone"
                label={t("ModalEditTicket.GasStation")}
                style={{ marginBottom: "0px" }}
                whiteBackground
                registerName="gasStationZone"
                register={register}
                onChange={(e) => setDefaultGasStationZone(e.target.value)}
                optionValue={ticketForEdit?.gasStationZone.number}
                defaultOption={
                  ticketForEdit?.gasStationZone?.number +
                  "-" +
                  ticketForEdit?.gasStationZone?.name
                }
                options={ownersGasStationZonesForEditingTicket?.map(
                  (gs, index) => {
                    if (
                      gs.gasStationZoneNumber ===
                      ticketForEdit?.gasStationZone?.number
                    ) {
                      return;
                    }
                    return (
                      <option key={index + 1} value={gs.gasStationZoneNumber}>
                        {gs.gasStationZoneNumber + " - " + gs.gasStationZoneName}
                      </option>
                    );
                  }
                )}
              />
              {ticketForEdit?.gasStationZone?.number !==
                defaultGasStationZone && (
                <div
                  style={{
                    border: "1px solid black",
                    padding: "5px",
                    marginTop: "5px",
                    borderRadius: "5px",
                  }}
                >
                  <EntityDetail
                    label={t("GasStations.GS name")}
                    value={gsZone?.gasStationZoneName}
                  />
                  <EntityDetail
                    label={t("GasStations.GS number")}
                    value={gsZone?.gasStationZoneNumber}
                  />
                  <EntityDetail
                    label={t("GasStations.GS address")}
                    value={gsZone?.address}
                  />
                  <EntityDetail
                    label={t("GasStations.additionalFee")}
                    value={gsZone?.additionalFee}
                  />
                  <EntityDetail
                    label={t("GasStations.paymentPeriod")}
                    value={gsZone?.paymentPeriod}
                  />

                  <EntityDetail
                    label={t("GasStations.reminderPaymentPeriod")}
                    value={gsZone?.reminderPaymentPeriod}
                  />

                  <EntityDetail
                    label={t("GasStations.lastReminderPaymentPeriod")}
                    value={gsZone?.lastReminderPaymentPeriod}
                  />
                </div>
              )}
            </>
          )}
          {defaultParkingZone && (
            <>
              <SelectWithLabel
                name="parkingZone"
                label={t("ModalEditTicket.ParkingZone")}
                style={{ marginBottom: "0px" }}
                whiteBackground
                registerName="parkingZone"
                register={register}
                changeHandler={(e) => setDefaultParkingZone(e.target.value)}
                optionValue={ticketForEdit?.parkingZone.parkingZoneNumber}
                defaultOption={
                  ticketForEdit?.parkingZone?.parkingZoneNumber +
                  "-" +
                  ticketForEdit?.parkingZone?.parkingZoneName
                }
                options={ownersParkingZonesForEditingTicket?.map((pz, index) => {
                  if (
                    pz.parkingZoneNumber ===
                    ticketForEdit?.parkingZone?.parkingZoneNumber
                  ) {
                    return;
                  }
                  return (
                    <option key={index + 1} value={pz.parkingZoneNumber}>
                      {pz.parkingZoneNumber + " - " + pz.parkingZoneName}
                    </option>
                  );
                })}
              />
              {ticketForEdit?.parkingZone?.parkingZoneNumber !=
                defaultParkingZone && (
                <div
                  style={{
                    border: "1px solid black",
                    padding: "5px",
                    marginTop: "5px",
                    borderRadius: "5px",
                  }}
                >
                  <EntityDetail
                    label={t("ParkingZones.PZ name")}
                    value={pzZone?.parkingZoneName}
                  />
                  <EntityDetail
                    label={t("ParkingZones.PZ number")}
                    value={pzZone?.parkingZoneNumber}
                  />
                  <EntityDetail
                    label={t("ParkingDetails.Parking zone address")}
                    value={pzZone?.address}
                  />
                  <EntityDetail
                    label={t("ParkingZones.price")}
                    value={pzZone?.price}
                  />
                  <EntityDetail
                    label={t("ParkingZones.paymentPeriod")}
                    value={pzZone?.paymentPeriod}
                  />

                  <EntityDetail
                    label={t("ParkingZones.reminderPaymentPeriod")}
                    value={pzZone?.reminderPaymentPeriod}
                  />

                  <EntityDetail
                    label={t("ParkingZones.lastReminderPaymentPeriod")}
                    value={pzZone?.lastReminderPaymentPeriod}
                  />
                </div>
              )}
            </>
          )}

          <div>
            <LabelWithHR label={t("ModalEditTicket.date")} />
            <Controller
              name="additionalCost"
              control={control}
              render={() => (
                <DatePicker
                  selected={date}
                  className={Style.textStyleInputEditModal}
                  showTimeSelect
                  required
                  locale="de"
                  dateFormat="Pp"
                  onChange={(date) => setDate(date)}
                  onCalendarOpen={() => setPickerHeight(300)}
                  onCalendarClose={() => setPickerHeight(50)}
                  timeIntervals={1}
                />
              )}
            />
          </div>

          <div>
            <LabelWithHR label={t("ModalEditTicket.paymentPeriod")} />
            <Controller
              name="paymentPeriod"
              control={control}
              render={() => (
                <input
                  className={Style.textStyleInputEditModal}
                  name="paymentPeriod"
                  onChange={(e) => {
                    setPaymentPeriod(e.target.value);
                  }}
                  defaultValue={ticketForEdit?.paymentPeriod}
                />
              )}
            />
          </div>

          {ticketForEdit?.reminderDeadline && (
            <div>
              <LabelWithHR label={t("ModalEditTicket.reminderPaymentPeriod")} />
              <Controller
                name="reminderPaymentPeriod"
                control={control}
                render={() => (
                  <input
                    className={Style.textStyleInputEditModal}
                    name="reminderPaymentPeriod"
                    onChange={(e) => {
                      setReminderPaymentPeriod(e.target.value);
                    }}
                    defaultValue={ticketForEdit?.reminderPaymentPeriod}
                  />
                )}
              />
            </div>
          )}

          {ticketForEdit?.lastReminderDeadline && (
            <div>
              <LabelWithHR label={t("ModalEditTicket.lastReminderPaymentPeriod")} />
              <Controller
                name="lastReminderPaymentPeriod"
                control={control}
                render={() => (
                  <input
                    className={Style.textStyleInputEditModal}
                    name="lastReminderPaymentPeriod"
                    onChange={(e) => {
                      setLastReminderPaymentPeriod(e.target.value);
                    }}
                    defaultValue={ticketForEdit?.lastReminderPaymentPeriod}
                  />
                )}
              />
            </div>
          )}

          <div className={Style.buttonContainer} style={{ display: 'flex', justifyContent: 'space-between', marginTop: pickerHeight }}>
            {defaultGasStationZone && (
              <Button
                style={{
                  width: "49%"
                }}
                buttonStyle={{
                  width: "100%",
                }}
                text={t("DO Details")}
                type="button"
                onClick={showDODetailsModal}
              />
            )}
            <Button
              style={{
                width: defaultGasStationZone ? "49%" : "100%"
              }}
              buttonStyle={{
                width: "100%",
              }}
              text={t("ModalEditTicket.save")}
              disabled={paymentPeriod < 0 || reminderPaymentPeriod < 0 || !plateNumber || plateNumber.length < 3}
            />
          </div>
        </form>
      </Modal>

      <DriveOffDetailsModal
        open={doDetailsModalOpen}
        close={closeDODetailsModal}
        onSave={handleDODetailsSave}
        countryCode={ticketForEdit?.gasStationZone?.countryCode}
        initialData={gasStationTicketDetails}
      />

      {plateConflict && (
        <ModalPlateConflict
          open={!!plateConflict}
          conflict={plateConflict}
          currentTicket={ticketForEdit}
          editData={pendingEditData}
          editTicket={props.editTicket}
          cancelTicket={props.cancelTicket}
          onCancelledCurrent={props.close}
          onClose={() => setPlateConflict(null)}
        />
      )}
    </>
  );
};
export default ModalEditTicket;